// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from './services/api';

export const AuthContext = createContext();

// --- helpers ---
const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const normalizeUser = (decoded) => {
  if (!decoded || typeof decoded !== 'object') return null;
  return {
    ...decoded,
    roles: asArray(decoded.roles ?? decoded.role),
    permissions: asArray(decoded.permissions),
  };
};
const msUntilExp = (decoded) =>
  decoded?.exp ? decoded.exp * 1000 - Date.now() : -1;

export function AuthProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTried, setRefreshTried] = useState(false);

  // ---- View As (Admin only) ----
  const [overrideRoles, setOverrideRoles] = useState(null);
  const [overridePermissions, setOverridePermissions] = useState(null);

  // Effective RBAC (view-as wins)
  const effectiveRoles = useMemo(() => {
    if (overrideRoles) return overrideRoles;
    if (user?.roles) return user.roles;
    if (user?.role) return [user.role];
    return [];
  }, [overrideRoles, user]);

  const effectivePermissions = useMemo(
    () => overridePermissions || user?.permissions || [],
    [overridePermissions, user]
  );

  useEffect(() => {
    console.debug('[AuthContext] State change:', { isLoggedIn, effectiveRoles, user, isLoading });
  }, [isLoggedIn, effectiveRoles, user, isLoading]);

  // token storage utils
  const getStoredToken = () =>
    localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

  const saveTokenToStorage = (accessToken) => {
    if (localStorage.getItem('accessToken')) {
      localStorage.setItem('accessToken', accessToken);
    } else if (sessionStorage.getItem('accessToken')) {
      sessionStorage.setItem('accessToken', accessToken);
    } else {
      localStorage.setItem('accessToken', accessToken);
    }
  };

  // Main effect: check token on mount or refresh
  useEffect(() => {
    let didCancel = false;

    async function checkAuth() {
      setIsLoading(true);
      
      // Skip auth check on public routes that don't need authentication
      const currentPath = window.location.pathname;
      const publicRoutes = ['/reset-password', '/forgot-password'];
      
      if (publicRoutes.includes(currentPath)) {
        console.log('[AuthContext] Skipping auth check for public route:', currentPath);
        if (!didCancel) {
          setIsLoggedIn(false);
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
      
      const token = getStoredToken();

      // If we have a token and it's still valid (or not about to die), use it
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const remaining = msUntilExp(decoded);
          if (remaining > 60_000) { // > 60s left feels safe
            if (!didCancel) {
              setIsLoggedIn(true);
              setUser(normalizeUser(decoded));
              setIsLoading(false);
            }
            return;
          }
        } catch (err) {
          localStorage.removeItem('accessToken');
          sessionStorage.removeItem('accessToken');
          console.error('[AuthContext] Invalid token, removed:', err);
        }
      }

      // Only try refresh once per app load
      if (refreshTried) { setIsLoading(false); return; }
      setRefreshTried(true);

      try {
        const response = await api.post('/auth/refresh-token', {}, { withCredentials: true, timeout: 4000 });
        const newAccessToken = response.data?.accessToken;
        if (!newAccessToken) throw new Error('No accessToken in response');

        const decoded = jwtDecode(newAccessToken);
        saveTokenToStorage(newAccessToken);
        if (!didCancel) {
          setIsLoggedIn(true);
          setUser(normalizeUser(decoded));
        }
      } catch (err) {
        if (!didCancel) {
          setIsLoggedIn(false);
          setUser(null);
        }
        localStorage.removeItem('accessToken');
        sessionStorage.removeItem('accessToken');
      } finally {
        if (!didCancel) setIsLoading(false);
      }
    }

    checkAuth();
    return () => { didCancel = true; };
  }, [refreshTried]);

  // Login handler accepts either a token or a pre-decoded user (or both)
  // login({ token, user, rememberMe })
  const login = (loginData) => {
    try {
      const { token, user: userObj, rememberMe = false } = loginData || {};

      if (!token && !userObj) throw new Error('login() requires token or user');

      // Store token when given
      if (token) {
        if (rememberMe) {
          localStorage.setItem('accessToken', token);
          sessionStorage.removeItem('accessToken');
        } else {
          sessionStorage.setItem('accessToken', token);
          localStorage.removeItem('accessToken');
        }
      }

      // Prefer decoded-from-token if token provided; else trust provided user
      const nextUser = token ? normalizeUser(jwtDecode(token)) : normalizeUser(userObj);

      setIsLoggedIn(true);
      setUser(nextUser);
      setIsLoading(false);
      setOverrideRoles(null);
      setOverridePermissions(null);
      console.debug('[AuthContext] login() set state:', nextUser, '| rememberMe:', rememberMe);
    } catch (err) {
      localStorage.removeItem('accessToken');
      sessionStorage.removeItem('accessToken');
      console.error('[AuthContext] login() failed:', err);
      setIsLoggedIn(false);
      setUser(null);
      setIsLoading(false);
    }
  };

  // Logout handler
  const logout = () => {
    localStorage.removeItem('accessToken');
    sessionStorage.removeItem('accessToken');
    setIsLoggedIn(false);
    setUser(null);
    setIsLoading(false);
    setRefreshTried(false);
    setOverrideRoles(null);
    setOverridePermissions(null);
    console.debug('[AuthContext] User logged out.');
  };

  // Manual refresh
  const refreshAuth = async () => {
    setIsLoading(true);
    const token = getStoredToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const remaining = msUntilExp(decoded);
        if (remaining > 60_000) {
          setIsLoggedIn(true);
          setUser(normalizeUser(decoded));
          setIsLoading(false);
          return;
        }
      } catch {
        // fall through to refresh
      }
    }
    try {
      const response = await api.post('/auth/refresh-token', {}, { withCredentials: true, timeout: 4000 });
      const newAccessToken = response.data?.accessToken;
      if (!newAccessToken) throw new Error('No accessToken in response');
      const decoded = jwtDecode(newAccessToken);
      saveTokenToStorage(newAccessToken);
      setIsLoggedIn(true);
      setUser(normalizeUser(decoded));
    } catch (err) {
      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem('accessToken');
      sessionStorage.removeItem('accessToken');
    }
    setIsLoading(false);
  };

  // RBAC helpers
  const hasRole = (roleName) => effectiveRoles.includes(roleName);
  const hasPermission = (permName) => effectivePermissions.includes(permName);

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      user,
      roles: effectiveRoles,
      permissions: effectivePermissions,
      isLoading,
      login,
      logout,
      refreshAuth,
      hasRole,
      hasPermission,
      // View-As/Admin
      overrideRoles, setOverrideRoles,
      overridePermissions, setOverridePermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
